import React, { useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { useStore } from 'react-redux'
import type { Action } from 'redux'
import { diff } from 'deep-diff'

import { subscribeToActions } from 'middlewares/actionRecorderMiddleware'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import useStateWithSetters from 'hooks/useStateWithSetters'
import Pane from 'components/Common/Pane'
import TextButton from 'LuxKit/Button/TextButton'
import Group from 'components/utils/Group'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'

import { HorizontalLayout, Content, Sidebar, VerticalLayout, Topbar } from '../../common'
import ExpandableObjectView from '../../ExpandableObjectView'
import ExpandableDiffView from '../../ExpandableDiffView'

const ActionListItem = styled(ClickableListItem)`
  max-width: ${rem(400)};
  &:not(:disabled):focus {
    outline: 0;
  }
`

const TextTab = styled(TextLink)`
  &:not(:disabled):focus {
    outline: 0;
  }
`

const ActionText = styled.pre`
  margin: 0;
  text-overflow: ellipsis;
  overflow: hidden;
`

interface RecordedAction {
  action: Action
  prevState: App.State
  nextState: App.State
}

const tabs = [
  { id: 'action', label: 'Action' },
  { id: 'state', label: 'State' },
  { id: 'diff', label: 'Diff' },
] as const

const tabIds = tabs.map(tab => tab.id)

function mapAction(action: Action) {
  if ('api' in action && typeof action.api === 'string') {
    return {
      ...action,
      type: `${action.api} - ${action.type.replace('API_CALL_', '')}`,
    }
  }
  return action
}

function ReduxActionsTab() {
  const [currentTabId, setTab] = useStateWithSetters(tabIds, 'action')
  const store = useStore<App.State>()
  const [actions, setActions] = useState<Array<RecordedAction>>(() => {
    const state = store.getState()
    return [{
      action: { type: '@@INIT' },
      prevState: state,
      nextState: state,
    }]
  })
  const [selectedActionIndex, setSelectedActionIndex] = useState<number>(0)

  const selectedAction = actions[selectedActionIndex]

  useEffect(() => {
    const unsubscribe = subscribeToActions((action, prevState, nextState) => {
      setActions((prev) => [...prev, { action: mapAction(action), prevState, nextState }])
    })

    return unsubscribe
  }, [store])

  const clearActions = useCallback(() => {
    const state = store.getState()
    setActions([{
      action: { type: '@@INIT' },
      prevState: state,
      nextState: state,
    }])
    setSelectedActionIndex(0)
  }, [store])

  const stateDiffs = useMemo(() => {
    if (!selectedAction) return undefined
    return diff(selectedAction.prevState, selectedAction.nextState)
  }, [selectedAction])

  return <HorizontalLayout>
    <Sidebar>
      {actions.map((a, i) => (
        <ActionListItem key={i} onClick={() => setSelectedActionIndex(i)} title={<ActionText title={a.action.type}>{a.action.type}</ActionText>} selected={selectedActionIndex === i} />
      ))}
      {actions.length > 1 && <Pane padding="12 16">
        <TextButton kind="secondary" onClick={clearActions}>Clear actions</TextButton>
      </Pane>}
    </Sidebar>

    <Content>
      {selectedAction && (
        <VerticalLayout>
          <Topbar>
            <Pane padding="12 20">
              <Group direction="horizontal" horizontalAlign="start" gap={12}>
                {tabs.map(tab => {
                  const isSelected = tab.id === currentTabId
                  return (
                    <TextTab key={tab.id} onClick={setTab[tab.id]} weight={isSelected ? 'bold' : 'regular'} underline={isSelected}>{tab.label}</TextTab>
                  )
                })}
              </Group>
            </Pane>
          </Topbar>

          <Content>
            <Pane padding={20}>
              {currentTabId === 'action' && <ExpandableObjectView value={selectedAction.action} initialExpanded />}
              {currentTabId === 'state' && <ExpandableObjectView value={selectedAction.nextState} initialExpanded />}
              {currentTabId === 'diff' && <>
                {stateDiffs && stateDiffs.length > 0 && <ExpandableDiffView diffs={stateDiffs} initialExpanded />}
                {(!stateDiffs || stateDiffs.length === 0) && <BodyText variant="large" italics colour="neutral-three">State unchanged</BodyText>}
              </>}
            </Pane>
          </Content>
        </VerticalLayout>
      )}
    </Content>
  </HorizontalLayout>
}

export default ReduxActionsTab
